"use client"

import * as React from "react"
import { Info, HelpCircle, AlertCircle } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"
import { cn } from "@/lib/utils"

export interface InfoTooltipProps {
  /** The content to display in the tooltip */
  content: React.ReactNode
  /** The side where the tooltip should appear */
  side?: "top" | "right" | "bottom" | "left"
  /** Delay before showing tooltip in milliseconds */
  delayDuration?: number
  /** Icon variant to display */
  variant?: "info" | "help" | "warning"
  /** Size of the icon */
  size?: "sm" | "md" | "lg"
  /** Custom className for the trigger icon */
  className?: string
  /** Whether to use a more subtle appearance */
  subtle?: boolean
  /** Custom icon to use instead of default */
  icon?: React.ComponentType<{ className?: string }>
  /** Additional props for the tooltip content */
  contentProps?: React.ComponentProps<typeof TooltipContent>
}

const iconVariants = {
  info: Info,
  help: HelpCircle,
  warning: AlertCircle,
}

const sizeVariants = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4", 
  lg: "h-5 w-5",
}

const colorVariants = {
  info: {
    default: "text-blue-500 hover:text-blue-600",
    subtle: "text-gray-400 hover:text-gray-600",
  },
  help: {
    default: "text-slate-500 hover:text-slate-600",
    subtle: "text-gray-400 hover:text-gray-600",
  },
  warning: {
    default: "text-amber-500 hover:text-amber-600",
    subtle: "text-gray-400 hover:text-gray-600",
  },
}

export function InfoTooltip({
  content,
  side = "top",
  delayDuration = 300,
  variant = "info",
  size = "md",
  className,
  subtle = false,
  icon,
  contentProps,
  ...props
}: InfoTooltipProps) {
  const IconComponent = icon || iconVariants[variant]
  const colorClass = colorVariants[variant][subtle ? "subtle" : "default"]
  const sizeClass = sizeVariants[size]

  return (
    <Tooltip delayDuration={delayDuration} {...props}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full",
            "cursor-default",
            colorClass,
            className
          )}
          aria-label="More information"
        >
          <IconComponent className={cn(sizeClass, "transition-transform hover:scale-110")} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className={cn(
          "max-w-xs p-3 text-sm",
          "bg-slate-900 text-white border-slate-800",
          "shadow-lg shadow-slate-900/20",
          "animate-in fade-in-0 zoom-in-95 duration-300",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-200"
        )}
        sideOffset={8}
        {...contentProps}
      >
        {typeof content === 'string' ? <p>{content}</p> : content}
      </TooltipContent>
    </Tooltip>
  )
}

// Convenience wrapper for common use cases
export function FieldTooltip({ 
  children, 
  tooltip, 
  ...props 
}: { 
  children: React.ReactNode
  tooltip: string | React.ReactNode
} & Omit<InfoTooltipProps, 'content'>) {
  return (
    <div className="flex items-center gap-2">
      {children}
      <InfoTooltip content={tooltip} {...props} />
    </div>
  )
}
